import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import styles from "./OrderForm.module.css";

function OrderForm() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    organization: "МодифиК",
    customer: "",
    date: "",
    dateFrom: "",
    region: "",
    address1: "",
    address2: "",
    goods: "Сценическое оборудование",
    weight: "",
    comment: "",
  });

  const [dateError, setDateError] = useState("");
  const [dataGroup1, setDataGroup1] = useState([]);
  const [dataGroup2, setDataGroup2] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // success modal
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("Заявка успешно создана");

  // inline error alert
  const [errorTitle, setErrorTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const clearError = () => {
    setErrorTitle("");
    setErrorMessage("");
  };

  // auth guard
  useEffect(() => {
    if (!currentUser) navigate("/");
  }, [currentUser, navigate]);

  // default datetime (now) for date/dateFrom
  useEffect(() => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const hh = String(now.getHours()).padStart(2, "0");
    const mi = String(now.getMinutes()).padStart(2, "0");
    const minDateTime = `${yyyy}-${mm}-${dd}T${hh}:${mi}`;

    setFormData((prev) => ({
      ...prev,
      date: prev.date || minDateTime,
      dateFrom: prev.dateFrom || minDateTime,
    }));
  }, []);

  // load select data
  useEffect(() => {
    const fetchJson = async (url) => {
      try {
        const resp = await fetch(url);
        if (!resp.ok) return [];
        return await resp.json();
      } catch {
        return [];
      }
    };

    const load = async () => {
      try {
        const [group1, group2] = await Promise.all([
          fetchJson(
            "https://1c.mf-group.com/07e10eae-630c-4115-8f43-c7e4ceb01d10/modific_events.json"
          ),
          fetchJson(
            "https://1c.mf-group.com/07e10eae-630c-4115-8f43-c7e4ceb01d10/energo_events.json"
          ),
        ]);

        setDataGroup1(group1);
        setDataGroup2(group2);

        const initial = group1?.[0];
        if (initial) {
          setFormData((prev) => ({
            ...prev,
            customer: initial.name || initial,
          }));
        }
      } finally {
        setLoadingData(false);
      }
    };

    load();
  }, []);

  // update customer when org changes
  useEffect(() => {
    const data = formData.organization === "МодифиК" ? dataGroup1 : dataGroup2;
    if (data?.length > 0) {
      setFormData((prev) => ({
        ...prev,
        customer: data[0].name || data[0],
      }));
    }
  }, [formData.organization, dataGroup1, dataGroup2]);

  // validation
  const isFormValid = useMemo(() => {
    const requiredFields = [
      formData.customer,
      formData.date,
      formData.dateFrom,
      formData.region,
      formData.address1,
      formData.weight,
    ];

    const allRequiredFilled = requiredFields.every(
      (v) => v !== null && v !== undefined && String(v).trim() !== ""
    );

    const isWeightValid =
      formData.weight && !isNaN(formData.weight) && Number(formData.weight) > 0;

    if (formData.date && formData.dateFrom) {
      const pickup = new Date(formData.date);
      const ret = new Date(formData.dateFrom);
      return allRequiredFilled && isWeightValid && ret >= pickup;
    }

    return allRequiredFilled && isWeightValid;
  }, [formData]);

  useEffect(() => {
    if (!formData.date || !formData.dateFrom) {
      setDateError("");
      return;
    }

    const pickup = new Date(formData.date);
    const ret = new Date(formData.dateFrom);

    if (ret < pickup) {
      setDateError("Дата возврата не может быть раньше даты поездки");
    } else {
      setDateError("");
    }
  }, [formData.date, formData.dateFrom]);

  // redirect after success
  useEffect(() => {
    if (!successOpen) return;
    const t = setTimeout(() => navigate("/dashboard"), 3000);
    return () => clearTimeout(t);
  }, [successOpen, navigate]);

  const getCurrentSelectData = () =>
    formData.organization === "МодифиК" ? dataGroup1 : dataGroup2;

  const handleChange = (e) => {
    const { name, value } = e.target;

    // if pickup date becomes later than return date -> align return date
    if (
      name === "date" &&
      formData.dateFrom &&
      new Date(value) > new Date(formData.dateFrom)
    ) {
      setFormData((prev) => ({ ...prev, date: value, dateFrom: value }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleOrganizationChange = (value) => {
    setFormData((prev) => ({ ...prev, organization: value }));
  };

  async function safeReadResponse(response) {
    const ct = response.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      try {
        return await response.json();
      } catch {
        return {};
      }
    }
    try {
      const text = await response.text();
      return { details: text };
    } catch {
      return {};
    }
  }

  function normalizeErrorMessage(status, data) {
    const raw =
      data?.details ||
      data?.error ||
      data?.message ||
      (typeof data === "string" ? data : "") ||
      "";

    if (status === 403) return raw || "Недостаточно прав";
    if (status === 401) return raw || "Требуется авторизация";
    if (status >= 500) return raw || "Ошибка сервера";
    return raw || "Ошибка при отправке";
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();
    setSuccessOpen(false);

    if (!isFormValid || submitting) {
      setErrorTitle("Проверьте поля");
      setErrorMessage("Заполните обязательные поля корректно.");
      return;
    }

    setSubmitting(true);

    try {
      const resp = await fetch("/api/order/create-order", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          organization: formData.organization,
          customer: formData.customer,
          date: formData.date,
          dateFrom: formData.dateFrom,
          region: formData.region,
          address1: formData.address1,
          address2: formData.address2,
          goods: formData.goods,
          weight: formData.weight,
          comment: formData.comment,
        }),
      });

      const data = await safeReadResponse(resp);

      if (resp.ok) {
        const msg = data?.details || data?.message || "Заявка успешно создана";
        setSuccessMessage(typeof msg === "string" ? msg : JSON.stringify(msg));
        setSuccessOpen(true);

        // optional: partial reset (оставим даты как есть, customer по орг — актуальный)
        setFormData((prev) => ({
          ...prev,
          region: "",
          address1: "",
          address2: "",
          goods: "Сценическое оборудование",
          weight: "",
          comment: "",
          // customer не трогаем
        }));
      } else {
        setErrorTitle("Не удалось отправить");
        setErrorMessage(normalizeErrorMessage(resp.status, data));
      }
    } catch (err) {
      setErrorTitle("Ошибка сети");
      setErrorMessage(
        "Не удалось связаться с сервером. " + (err?.message || "")
      );
    } finally {
      setSubmitting(false);
    }
  };

  // if (!currentUser) return <div>Загрузка...</div>;
  // if (loadingData) return <div className={styles.container}>Загрузка данных...</div>;

  const weightInvalid =
    formData.weight && (isNaN(formData.weight) || Number(formData.weight) <= 0);

  return (
    <div className={styles.container}>
      {successOpen && (
        <div className={styles.successOverlay} role="dialog" aria-modal="true">
          <div className={styles.successModal}>
            <div className={styles.successIcon} aria-hidden="true" />
            <h3>Успешно!</h3>
            <p>{successMessage}</p>
            <div className={styles.progressBar}>
              <div className={styles.progressFill} />
            </div>
            <p className={styles.redirectText}>Переход в кабинет через 3 секунды...</p>
          </div>
        </div>
      )}

      <div className={styles.formContainer}>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.topControls}>
            <div className={styles.toggle}>
              <input
                type="radio"
                name="organization"
                id="option1"
                value="МодифиК"
                checked={formData.organization === "МодифиК"}
                onChange={() => handleOrganizationChange("МодифиК")}
                className={styles.toggleInput}
              />
              <label
                className={`${styles.toggleLabel} ${
                  formData.organization === "МодифиК" ? styles.active : ""
                }`}
                htmlFor="option1"
              >
                МодифиК
              </label>

              <input
                type="radio"
                name="organization"
                id="option2"
                value="МФ-Энерго"
                checked={formData.organization === "МФ-Энерго"}
                onChange={() => handleOrganizationChange("МФ-Энерго")}
                className={styles.toggleInput}
              />
              <label
                className={`${styles.toggleLabel} ${
                  formData.organization === "МФ-Энерго" ? styles.active : ""
                }`}
                htmlFor="option2"
              >
                МФ Энерго
              </label>
            </div>

            <button
              onClick={() => navigate("/dashboard")}
              className={styles.backButton}
              type="button"
            >
              Назад
            </button>
          </div>

          <div className={styles.fieldsGrid}>
            <div className={styles.inputField}>
              <label className={styles.label} htmlFor="customer">
                Заказчик: *
              </label>
              <select
                id="customer"
                name="customer"
                value={formData.customer}
                onChange={handleChange}
                className={styles.select}
                required
              >
                {getCurrentSelectData().map((item, idx) => (
                  <option key={idx} value={item.name || item}>
                    {item.name || item}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.inputField}>
              <label className={styles.label} htmlFor="region">
                Регион: *
              </label>
              <input
                type="text"
                id="region"
                name="region"
                value={formData.region}
                onChange={handleChange}
                className={styles.input}
                required
              />
            </div>

            <div className={styles.inputField}>
              <label className={styles.label} htmlFor="address1">
                Адрес назначения: *
              </label>
              <input
                type="text"
                id="address1"
                name="address1"
                value={formData.address1}
                onChange={handleChange}
                className={styles.input}
                required
              />
            </div>

            <div className={styles.inputField}>
              <label className={styles.label} htmlFor="address2">
                Второй адрес назначения (если нужен):
              </label>
              <input
                type="text"
                id="address2"
                name="address2"
                value={formData.address2}
                onChange={handleChange}
                className={styles.input}
              />
            </div>

            <div className={styles.inputField}>
              <label className={styles.label} htmlFor="goods">
                Вид оборудования:
              </label>
              <select
                id="goods"
                name="goods"
                value={formData.goods}
                onChange={handleChange}
                className={styles.select}
              >
                <option value="Сценическое оборудование">Сценическое оборудование</option>
                <option value="Электротехническое оборудование">
                  Электротехническое оборудование
                </option>
                <option value="Хозяйственные товары">Хозяйственные товары</option>
                <option value="Металопрокат">Металопрокат</option>
                <option value="ЖБИ">ЖБИ</option>
              </select>
            </div>

            <div className={styles.inputField}>
              <label className={styles.label} htmlFor="weight">
                Вес в килограммах: *
              </label>
              <input
                type="number"
                id="weight"
                name="weight"
                value={formData.weight}
                onChange={handleChange}
                className={`${styles.input} ${weightInvalid ? styles.inputError : ""}`}
                required
                min="1"
              />
              {weightInvalid && (
                <div className={styles.dateError}>
                  Введите корректный вес (больше 0)
                </div>
              )}
            </div>

            <div className={`${styles.inputField} ${styles.fullWidthField}`}>
              <label className={styles.label} htmlFor="comment">
                Комментарий к заявке:
              </label>
              <input
                type="text"
                id="comment"
                name="comment"
                value={formData.comment}
                onChange={handleChange}
                className={styles.input}
              />
            </div>
          </div>

          <div className={styles.datesGrid}>
            <div className={styles.inputField}>
              <label className={styles.label} htmlFor="date">
                Дата и время поездки: *
              </label>
              <input
                type="datetime-local"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                className={styles.input}
                required
                min={new Date().toISOString().slice(0, 16)}
              />
            </div>

            <div className={styles.inputField}>
              <label className={styles.label} htmlFor="dateFrom">
                Дата и время возврата: *
              </label>
              <input
                type="datetime-local"
                id="dateFrom"
                name="dateFrom"
                value={formData.dateFrom}
                onChange={handleChange}
                className={`${styles.input} ${dateError ? styles.inputError : ""}`}
                required
                min={formData.date || new Date().toISOString().slice(0, 16)}
              />
              {dateError && <div className={styles.dateError}>{dateError}</div>}
            </div>
          </div>

          {(errorTitle || errorMessage) && (
            <div className={`${styles.alert} ${styles.alertError}`}>
              <div>
                <p className={styles.alertText}>{errorTitle || "Ошибка"}</p>
                {errorMessage && <p className={styles.alertSubText}>{errorMessage}</p>}
              </div>
              <button type="button" className={styles.alertButton} onClick={clearError}>
                Понятно
              </button>
            </div>
          )}

          <button type="submit" className={styles.sendButton} disabled={!isFormValid || submitting}>
            {submitting ? "Отправка..." : "Заказать машину"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default OrderForm;